'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Copy, CheckCircle2, Leaf, Shield, BarChart3, Truck, Settings } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  icon: React.ElementType;
  color: string;
}

const demoCredentials: DemoCredential[] = [
  { role: 'Administration', email: 'superadmin@sonaged.sn', password: 'SuperAdmin@2026', icon: Settings, color: 'text-purple-600' },
  { role: 'Direction', email: 'direction@sonaged.sn', password: 'Sonaged@2026', icon: BarChart3, color: 'text-blue-600' },
  { role: 'Resp. QHSE', email: 'qhse@sonaged.sn', password: 'Qhse@2026', icon: Shield, color: 'text-amber-600' },
  { role: 'Resp. Collecte', email: 'collecte@sonaged.sn', password: 'Collecte@2026', icon: Truck, color: 'text-green-600' },
  { role: 'Agent Terrain', email: 'agent@sonaged.sn', password: 'Agent@2026', icon: Leaf, color: 'text-teal-600' },
];

const stats = [
  { label: 'Agents opérationnels', value: '1 000+' },
  { label: 'Tonnes traitées / an', value: '2.3M' },
  { label: 'Communes couvertes', value: '8' },
  { label: 'Sites de traitement', value: '12' },
];

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { signIn, getUserProfile } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Connexion réussie — Bienvenue sur SONAGED');
      // Redirect super_admin to Super Administration page
      try {
        const userProfile = await getUserProfile();
        if (userProfile?.role === 'super_admin') {
          router.push('/super-administration');
        } else {
          router.push('/tableau-de-bord-dashboard');
        }
      } catch {
        router.push('/tableau-de-bord-dashboard');
      }
    } catch (err: any) {
      toast.error('Identifiants invalides — Vérifiez votre email et mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseCredential = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    toast.success(`Identifiants "${cred.role}" chargés`);
  };

  const handleCopy = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[56%] flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #064e3b 0%, #065f46 30%, #166534 65%, #16a34a 100%)' }}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Decorative circles */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #86efac, transparent)' }} />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #4ade80, transparent)' }} />

        <div className="relative z-10 flex flex-col h-full px-12 py-10">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/15 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20">
              <AppLogo size={32} />
            </div>
            <div>
              <p className="text-white font-bold text-xl tracking-tight">SONAGED</p>
              <p className="text-green-200 text-xs font-medium">Société Nationale de Gestion Intégrée des Déchets</p>
            </div>
          </div>

          {/* Hero text */}
          <div className="flex-1 flex flex-col justify-center max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full border border-white/20 text-green-200 text-xs font-semibold mb-6 w-fit">
              <Leaf size={12} />
              Plateforme Digitale Officielle — Sénégal
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              Plateforme Intégrée de Gestion des Déchets & Management QHSE
            </h1>
            <p className="text-green-100 text-base leading-relaxed mb-8">
              Digitaliser, tracer, mesurer et améliorer la gestion des déchets. Conformité ISO 9001 · ISO 14001 · ISO 45001.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat) => (
                <div key={`stat-${stat.label}`} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
                  <p className="text-white font-bold text-2xl font-tabular">{stat.value}</p>
                  <p className="text-green-200 text-xs mt-0.5 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 text-green-300 text-xs">
            <Shield size={12} />
            <span>Sous tutelle du Ministère de l&apos;Environnement et de la Transition écologique · Dakar, Sénégal</span>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-10 bg-background overflow-y-auto">
        <div className="max-w-md mx-auto w-full">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <div>
              <p className="font-bold text-lg text-foreground">SONAGED</p>
              <p className="text-muted-foreground text-xs">Gestion Intégrée des Déchets</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-1">Connexion</h2>
            <p className="text-muted-foreground text-sm">Accédez à votre espace de gestion SONAGED</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="email">
                Adresse e-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="votre.nom@sonaged.sn"
                className={`input-field w-full px-4 py-2.5 text-sm ${errors.email ? 'border-red-400 focus:border-red-500' : ''}`}
                {...register('email', {
                  required: 'L\'adresse e-mail est requise',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Format d\'e-mail invalide' },
                })}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="password">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  className={`input-field w-full px-4 py-2.5 pr-11 text-sm ${errors.password ? 'border-red-400' : ''}`}
                  {...register('password', {
                    required: 'Le mot de passe est requis',
                    minLength: { value: 6, message: 'Minimum 6 caractères' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.password.message}</p>
              )}
            </div>

            {/* Remember + forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-primary"
                  {...register('rememberMe')}
                />
                <span className="text-sm text-muted-foreground">Se souvenir de moi</span>
              </label>
              <button type="button" className="text-sm text-primary font-semibold hover:underline">
                Mot de passe oublié ?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Connexion en cours...</span>
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-8">
            <div className="relative flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">Comptes de démonstration</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {demoCredentials.map((cred) => {
                const IconComp = cred.icon;
                return (
                  <button
                    key={`demo-${cred.role}`}
                    type="button"
                    onClick={() => handleUseCredential(cred)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-colors text-left group"
                  >
                    <IconComp size={14} className={cred.color} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">{cred.role}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{cred.email}</p>
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); handleCopy(cred.password, cred.role); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleCopy(cred.password, cred.role); } }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      {copiedField === cred.role
                        ? <CheckCircle2 size={12} className="text-green-600" />
                        : <Copy size={12} className="text-muted-foreground" />
                      }
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © 2026 SONAGED S.A. · Dakar, Sénégal · Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
}